import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { Coinflip } from '@old-app/coinflip/interfaces/coinflip.interface';
import { CoinflipCoinComponent } from '@old-app/coinflip/coinflip-coin/coinflip-coin.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { CoinflipSpritesService } from '@old-app/coinflip/coinflip-sprites.service';

// Atlas geometry — single-column sheet of 90 frames at 350×350 px each.
// Matches the original CSS `@keyframes coin-spin` that ran 3 s with steps(90)
// and bg-pos-y 0 → -31500 px (90 × 350).
const ATLAS_COLS = 1;
const ATLAS_ROWS = 90;
const ATLAS_TILES = ATLAS_COLS * ATLAS_ROWS;
const ATLAS_TILE_SIZE = 350;
const ANIMATION_DURATION_MS = 3000;
const TILE_DURATION_MS = ANIMATION_DURATION_MS / ATLAS_TILES;

@Component({
  selector: 'app-cf-middle',
  templateUrl: './cf-middle.component.html',
  styleUrls: ['./cf-middle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CoinflipCoinComponent],
  animations: [fadeInAnimation],
})
export class CfMiddleComponent {
  readonly coinflip = input.required<Coinflip>();
  readonly showAnimation = input.required<boolean>();
  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('coinCanvas');

  private spritesService = inject(CoinflipSpritesService);
  private destroyRef = inject(DestroyRef);

  private rafHandle: number | null = null;
  // True between the moment we commit to starting a spin (synchronous, in
  // the effect) and the moment the spin is explicitly stopped. Critical for
  // ignoring spurious effect re-fires while `playing` is still true — those
  // re-fires would otherwise call stopSpin() and cancel the in-flight rAF,
  // freezing the animation mid-cycle. See the long comment on the effect
  // body below for the failure mode this guards against.
  private isSpinning = false;
  private hasPrimedCanvas = false;

  constructor() {
    effect(() => {
      const canvas = this.canvasRef()?.nativeElement;
      const playing = this.showAnimation();

      if (!canvas) return;

      // Idempotent state machine. Without this guard, if the effect re-fires
      // while `playing` is still true (e.g. the parent's view tree shuffles
      // and the input signal carries a fresh write of the same `true`, or
      // viewChild's signal re-emits, or whatever) the previous `stopSpin()`
      // call would cancel the in-flight rAF and the animation would freeze
      // until the next legitimate state change. We only act on real
      // transitions: not-spinning + playing → start; spinning + not-playing
      // → stop. Same-state re-fires are no-ops.
      if (playing && !this.isSpinning) {
        this.isSpinning = true;
        void this.startSpin(canvas);
      } else if (!playing && this.isSpinning) {
        this.isSpinning = false;
        this.stopSpin();
      } else if (!playing && !this.hasPrimedCanvas) {
        // First idle moment after the canvas mounts — prime the GPU pipeline
        // for both possible winners. See the comment on `primeCanvas`.
        this.hasPrimedCanvas = true;
        void this.primeCanvas(canvas);
      }
    });
    this.destroyRef.onDestroy(() => {
      this.isSpinning = false;
      this.stopSpin();
    });
  }

  /**
   * Pre-bind both spin atlases into this canvas's 2D context so the *first*
   * `drawImage(bitmap, …)` of the actual spin is a cheap blit instead of a
   * cold GPU texture upload.
   *
   * Subtlety: `createImageBitmap` returns a GPU-friendly handle, but each
   * `<canvas>` has its own rendering context, and the texture isn't actually
   * bound into that context until the first `drawImage` from the bitmap.
   * That first bind can take 50–100 ms on a freshly-mounted modal — and that
   * latency is paid on the same frame as `draw()`'s first call, which
   * delays the *next* rAF and causes the animation to visibly jump from
   * frame 0 to ~frame 7 on its second tick of the first viewing.
   *
   * Calling `drawImage` for both blue and red atlases here, while the canvas
   * is still hidden (no `--playing` class, opacity 0), pays the bind cost
   * during the countdown phase. We `clearRect` afterwards so nothing is left
   * on the canvas when the spin actually starts. Aborts if the spin starts
   * mid-prime (rare; only matters if the user reaches `running` before
   * `getBitmap()` resolves on app cold-start).
   */
  private async primeCanvas(canvas: HTMLCanvasElement): Promise<void> {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const internalSize = Math.round(ATLAS_TILE_SIZE * dpr);
    if (canvas.width !== internalSize || canvas.height !== internalSize) {
      canvas.width = internalSize;
      canvas.height = internalSize;
    }

    for (const side of [1, 2] as const) {
      if (this.showAnimation()) return;
      try {
        const bitmap = await this.spritesService.getBitmap(side);
        if (this.showAnimation()) return;
        ctx.drawImage(
          bitmap,
          0,
          0,
          ATLAS_TILE_SIZE,
          ATLAS_TILE_SIZE,
          0,
          0,
          internalSize,
          internalSize
        );
      } catch {
        // Bitmap load failure is handled at the spin site; nothing to do here.
      }
    }
    if (!this.showAnimation()) {
      ctx.clearRect(0, 0, internalSize, internalSize);
    }
  }

  private async startSpin(canvas: HTMLCanvasElement): Promise<void> {
    const side = this.coinflip().winner?.side;
    if (side !== 1 && side !== 2) {
      this.isSpinning = false;
      return;
    }

    const bitmap = await this.spritesService.getBitmap(side);
    // The spin may have been cancelled while we were waiting for the bitmap
    // (modal closed, animation flipped off). Bail without scheduling a draw.
    if (!this.isSpinning) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match the canvas's backing-store resolution to the device's pixel
    // density so the drawn tile is crisp on retina displays. We only resize
    // when the dimensions actually change to avoid clearing existing state
    // and to preserve any prior context transforms.
    const dpr = window.devicePixelRatio || 1;
    const internalSize = Math.round(ATLAS_TILE_SIZE * dpr);
    if (canvas.width !== internalSize || canvas.height !== internalSize) {
      canvas.width = internalSize;
      canvas.height = internalSize;
    }

    // Capture startTime INSIDE the first rAF callback, not before scheduling.
    // First-viewing work (GPU texture upload of the bitmap, audio-context
    // init from the just-fired playSound, Angular CD, modal layout) can push
    // the very first paint by 50–150 ms. If startTime were captured before
    // requestAnimationFrame, the first drawn frame would be `floor(delay /
    // 15.7ms)` — i.e. several tiles into the spin — making the animation
    // visibly start mid-cycle on the first view of a session. Capturing
    // startTime on the first paint pins frame 0 to whatever moment the
    // canvas first appears.
    let startTime: number | null = null;
    const draw = () => {
      if (startTime === null) {
        startTime = performance.now();
      }
      const elapsed = performance.now() - startTime;
      // The CSS animation this replaces ran two stepped sub-animations
      // simultaneously: a 6-step x-cycle inside every 94.1ms y-step, with y
      // walking 34 steps over 3.2s. Flattened, that's a 204-frame sequence
      // visited in row-major order — frame 0 is (col=0, row=0), frame 6 is
      // (col=0, row=1), and so on. Looping with `% ATLAS_TILES` matches the
      // original `infinite` repeat behaviour for any spin that runs past the
      // 3.2s cycle (the modal stops it well before that today).
      const frame = Math.floor(elapsed / TILE_DURATION_MS) % ATLAS_TILES;
      const xIdx = frame % ATLAS_COLS;
      const yIdx = Math.floor(frame / ATLAS_COLS) % ATLAS_ROWS;
      ctx.clearRect(0, 0, internalSize, internalSize);
      ctx.drawImage(
        bitmap,
        xIdx * ATLAS_TILE_SIZE,
        yIdx * ATLAS_TILE_SIZE,
        ATLAS_TILE_SIZE,
        ATLAS_TILE_SIZE,
        0,
        0,
        internalSize,
        internalSize
      );
      this.rafHandle = requestAnimationFrame(draw);
    };
    this.rafHandle = requestAnimationFrame(draw);
  }

  private stopSpin(): void {
    if (this.rafHandle != null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }
}
